import React, { createRef, useEffect, useState } from "react";
import { Flex, FormControl, FormErrorMessage, FormLabel, Input } from "@chakra-ui/react";
import {
  Box,
  Button,
  Checkbox,
  FileUpload,
  InlineNotification,
  Spinner,
  theme,
  useToast,
  useTranslation,
} from "@familyzone/component-library";
import CardBasedPage from "../../templates/CardBasedPage";
import Api from "../../../utils/Api";
import SectionHeading from "../../header/components/SectionHeading";
import "react-quill/dist/quill.snow.css";
import ReactQuill from "react-quill";
import { sanitize } from "dompurify";
import { BlockPageConfig, BlockPageConfigEquals } from "../../../types/BlockPage";
import BlockPageLogo from "./BlockPageLogo";
import CriteriaSelector from "../../../modules/criteria/CriteriaSelector";
import { FilteringSourceCriteria } from "../../../modules/criteria/criteriaTypes";

export const BLOCK_PAGE_CONFIG_URL = "/config/ajax/blockedpage";

const BlockPageConfiguration: React.FC = () => {
  const { t } = useTranslation();
  const { successToast, errorToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState<File>();

  const [savedConfig, setSavedConfig] = useState<BlockPageConfig>({ is_active: false });
  const [pageConfig, setPageConfig] = useState<BlockPageConfig>({ is_active: false });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const logoInputRef = createRef<HTMLInputElement>();
  const messageInputRef = createRef<ReactQuill>();

  useEffect(() => {
    void Api.getAsync(BLOCK_PAGE_CONFIG_URL).then(
      (result: Partial<BlockPageConfig>) => {
        const sanitized: BlockPageConfig = {
          ...result,
          // ensure boolean value: if `null`, convert to `false`
          is_active: !!result.is_active,
          message: sanitize(result.message ?? ""),
        };

        setSavedConfig({ ...sanitized });
        setPageConfig({ ...sanitized });
        setLoading(false);
      },
      () => {
        errorToast({
          title: t("Please Try Again"),
          description: t("Failed to load Block Page configuration"),
          isClosable: true,
        });
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (logoInputRef.current) {
      logoInputRef.current.id = "customLogoInput";
    }
  }, [logoInputRef]);

  useEffect(() => {
    if (messageInputRef.current) {
      // https://github.com/zenoamaro/react-quill/issues/663
      messageInputRef.current.getEditor().root.id = "customMessageInput";
      messageInputRef.current.getEditor().root.setAttribute("role", "textbox");
      messageInputRef.current.getEditor().root.setAttribute("aria-label", "Custom Message");

      if (!pageConfig.is_active) {
        messageInputRef.current.getEditor().root.setAttribute("aria-readonly", "true");
      }
    }
  }, [messageInputRef, pageConfig]);

  const removeUnsavedLogoFile = () => {
    setLogoFile(undefined);
    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }
  };

  const handlePageStatusChange = (): void => {
    setPageConfig((config) => {
      if (config.is_active) {
        removeUnsavedLogoFile();
        setFieldErrors({});
        return { is_active: false };
      } else if (savedConfig.is_active) {
        return { ...savedConfig };
      }
      return { is_active: true };
    });
  };

  const handleBypassStatusChange = (): void => {
    setPageConfig((config) => ({ ...config, hide_bypass_code: !config.hide_bypass_code }));
  };

  const handleAddLogo = (file: File, uploadComplete: (success: boolean) => void): void => {
    if (file.size / 1000 > 150) {
      setFieldErrors((errs) => ({ ...errs, logo: "Logo file size must be less than 150 KB" }));
      setPageConfig((config) => ({ ...config, logo: undefined }));
      removeUnsavedLogoFile();
    } else {
      setFieldErrors((errs) => ({ ...errs, logo: "" }));
      setPageConfig((config) => ({ ...config, logo: { name: file.name, content_type: file.type } }));
      setLogoFile(file);
    }

    // hide FileInput built-in upload status
    uploadComplete(true);
  };

  const handleRemoveLogo = () => {
    setFieldErrors((errs) => ({ ...errs, logo: "" }));
    setPageConfig((config) => ({ ...config, logo: undefined }));
    removeUnsavedLogoFile();
  };

  const handleMessageChange = (value: string): void => {
    setPageConfig((config) => {
      const newConfig = { ...config };
      if (!config.is_active) {
        delete newConfig.message;
      } else {
        newConfig.message = sanitize(value);
      }
      return newConfig;
    });
  };

  const handlePolicyLinkChange = (value: unknown): void => {
    setFieldErrors((errs) => ({ ...errs, internet_usage_policy: "" }));
    setPageConfig((config) => ({ ...config, internet_usage_policy: String(value) }));
  };

  const handleAuthorizedGroupsChanged = (criteria: FilteringSourceCriteria[]): void => {
    setPageConfig((config) => ({ ...config, permission_to_logout: { source_criteria: criteria } }));
  };

  const handleSave = () => {
    if (pageConfig.internet_usage_policy) {
      const errors: Record<string, string> = {};
      if (!pageConfig.internet_usage_policy.startsWith("https://")) {
        errors["internet_usage_policy"] = t("URL must start with https://");
      }
      if (pageConfig.internet_usage_policy.includes(" ")) {
        errors["internet_usage_policy"] = t("URL must not contain whitespace");
      }
      if (Object.keys(errors).length > 0) {
        setFieldErrors((errs) => ({ ...errs, ...errors }));
        return;
      }
    }

    setSaving(true);

    const formData = new FormData();
    formData.append("data", JSON.stringify(pageConfig));

    if (logoFile) {
      formData.append("logo", logoFile);
    }

    Api.post_file(
      "/config/ajax/blockedpage",
      formData,
      (result: BlockPageConfig) => {
        // clear in-memory file to trigger BlockPageLogo component to reload
        // the image from GCS bucket to validate that it was correctly uploaded
        setLogoFile(undefined);

        setSavedConfig((config) => ({ ...config, ...result }));
        setPageConfig((config) => ({ ...config, ...result }));

        successToast({
          title: t("Update Successful"),
          description: t("Block Page configuration has been successfully updated"),
          isClosable: true,
        });
        setSaving(false);
      },
      () => {
        errorToast({
          title: t("Please Try Again"),
          description: t("Failed to update Block Page configuration"),
          isClosable: true,
        });
        setSaving(false);
      }
    );
  };

  const hasChanges = !BlockPageConfigEquals(savedConfig, pageConfig);

  return (
    <CardBasedPage
      title={t("Block Page")}
      breadcrumbs={[
        { title: t("Configuration"), url: "/config", isActive: false },
        { title: t("Block Page"), isActive: true },
      ]}
    >
      <Box minHeight="200px" p="sp24" position="relative">
        <SectionHeading title={t("Block Page Configuration")} aria-level={2} />

        {loading && (
          <Flex minHeight="inherit" alignItems="center" justifyContent="center">
            <Spinner size="md" />
          </Flex>
        )}

        {!loading && (
          <Flex px="sp12" py="sp16" direction="column" gap="sp16">
            <FormControl isDisabled={loading || saving}>
              <Checkbox
                id="enableBlockPage"
                name="enableBlockPage"
                isChecked={pageConfig.is_active}
                isDisabled={loading || saving}
                onChange={handlePageStatusChange}
              >
                {t("Enable Custom Block Page")}
              </Checkbox>
            </FormControl>

            <FormControl isDisabled={loading || saving || !pageConfig.is_active}>
              <Checkbox
                id="hideBypassCode"
                name="hideBypassCode"
                isChecked={!!pageConfig.hide_bypass_code}
                isDisabled={loading || saving || !pageConfig.is_active}
                onChange={handleBypassStatusChange}
              >
                {t("Hide Bypass Code")}
              </Checkbox>
            </FormControl>

            <FormControl isDisabled={loading || saving || !pageConfig.is_active} isInvalid={!!fieldErrors.logo}>
              <FormLabel htmlFor="customLogoInput">{t("Custom Logo")}</FormLabel>
              <Flex gap="sp16" flexWrap="wrap-reverse">
                <Flex width="580px" direction="column" gap="sp8">
                  <FileUpload
                    ref={logoInputRef}
                    primaryText={t("Select an image or drag and drop here")}
                    secondaryText={t("Only .png, .jpg, .svg and .webp files under 150 KB are supported")}
                    accept="image/png,image/jpg,image/jpeg,image/svg+xml,image/webp"
                    onUpload={handleAddLogo}
                    disabled={loading || saving || !pageConfig.is_active}
                    autoUpload={true}
                    clearOnUpload={true}
                  />

                  <InlineNotification
                    status="info"
                    notificationDescription={
                      <Box color="text.title">
                        {t("It may take up to an hour for logo changes to reflect on Block Page for all users.")}
                      </Box>
                    }
                  />

                  <FormErrorMessage fontWeight="regular">{fieldErrors.logo}</FormErrorMessage>
                </Flex>
                <BlockPageLogo
                  bucketName={pageConfig.gcp_bucket_logo_location ?? ""}
                  logoName={pageConfig.logo?.name}
                  logoFile={logoFile}
                  disabled={loading || saving || !pageConfig.is_active}
                  onRemove={handleRemoveLogo}
                />
              </Flex>
            </FormControl>

            <FormControl isDisabled={loading || saving || !pageConfig.is_active} isInvalid={false}>
              <FormLabel htmlFor="customMessageInput" onClick={() => messageInputRef.current?.focus()}>
                {t("Custom Message")}
              </FormLabel>
              <Box
                css={
                  loading || saving || !pageConfig.is_active
                    ? {
                        pointerEvents: "none",
                        cursor: "not-allowed",
                        ".ql-toolbar *": {
                          opacity: 0.85,
                        },
                        ".ql-container": {
                          // eslint-disable-next-line
                          color: theme.colors.form.disabled.text,
                          // eslint-disable-next-line
                          backgroundColor: theme.colors.form.disabled.background,
                        },
                      }
                    : {}
                }
              >
                {/* `defaultValue` is used instead of `value` to avoid an infinite
                loop due to the editor randomly reordering inline styles
                https://familyzone.atlassian.net/browse/HIT-5865
                https://github.com/zenoamaro/react-quill/issues/1045*/}
                <ReactQuill
                  ref={messageInputRef}
                  defaultValue={pageConfig.message}
                  onChange={handleMessageChange}
                  readOnly={loading || saving || !pageConfig.is_active}
                />
              </Box>
            </FormControl>

            <FormControl isDisabled={loading || saving || !pageConfig.is_active} isInvalid={!!fieldErrors.internet_usage_policy}>
              <FormLabel htmlFor="internetUsagePolicyLink">{t("Internet Usage Policy Link")}</FormLabel>
              <Input
                id="internetUsagePolicyLink"
                isDisabled={loading || saving || !pageConfig.is_active}
                onChange={(e) => handlePolicyLinkChange(e.target.value)}
                value={pageConfig.internet_usage_policy ? String(pageConfig.internet_usage_policy) : ""}
              />
              <FormErrorMessage fontWeight="regular">{fieldErrors.internet_usage_policy}</FormErrorMessage>
            </FormControl>

            <FormControl isDisabled={loading || saving || !pageConfig.is_active} isInvalid={false}>
              <FormLabel htmlFor="criteria-selector">{t("Permission to Logout from the Block Page")}</FormLabel>
              <Box
                // FIXME: Update MultiSearchSelector to respect form control disabled theme like all other inputs...
                css={
                  loading || saving || !pageConfig.is_active
                    ? {
                        "> div > div": {
                          // eslint-disable-next-line
                          backgroundColor: theme.colors.form.disabled.background,
                        },
                      }
                    : {}
                }
              >
                <CriteriaSelector
                  selected={
                    Array.isArray(pageConfig.permission_to_logout?.source_criteria)
                      ? pageConfig.permission_to_logout?.source_criteria ?? []
                      : []
                  }
                  disabled={loading || saving || !pageConfig.is_active}
                  errors={[]}
                  onChange={handleAuthorizedGroupsChanged}
                  customOptions={{
                    "exclude.group": t("Exclude Group"),
                    group: t("Group"),
                  }}
                />
              </Box>
            </FormControl>
          </Flex>
        )}
      </Box>

      <Box p="sp24" backgroundColor="brand.100" borderBottomRadius="sm">
        <Button variant="primary" disabled={loading || saving || !hasChanges} onClick={handleSave}>
          {saving ? t("Saving..") : hasChanges ? t("Save") : t("Saved")}
        </Button>
      </Box>
    </CardBasedPage>
  );
};

export default BlockPageConfiguration;
